"use client"

import { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
} from "@mui/material"
import { Close as CloseIcon } from "@mui/icons-material"
import { toast } from "react-toastify"
import { API_URL } from "../../constants"

function CompareCampaignsDialog({
  open,
  onClose,
  onCompare,
  currentCampaignId,
  currentCampaignUserId,
  isComparing,
  userInfo,
  downloadLink,
  setDownloadLink,
}) {
  // Local state - completely independent of global Redux state
  const [localLoading, setLocalLoading] = useState(false)
  const [localError, setLocalError] = useState(null)
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([])
  const [availableCampaigns, setAvailableCampaigns] = useState([])
  const [commonNumbers, setCommonNumbers] = useState([])

  // Fetch campaigns directly using fetch API instead of Redux
  const fetchCampaignsForComparison = async () => {
    if (!currentCampaignUserId || !userInfo?.token) return

    setLocalLoading(true)
    setLocalError(null)

    try {
      const url = `${API_URL}/api/campaigns?status=finished&userId=${currentCampaignUserId}`

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userInfo.token}`,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch campaigns: ${response.statusText}`)
      }

      const data = await response.json()

      // Filter out the current campaign and ensure they are finished
      const filtered = (data.campaigns || []).filter(
        (camp) => camp._id !== currentCampaignId && camp.status === "finished",
      )

      setAvailableCampaigns(filtered)
    } catch (error) {
      console.error("Failed to fetch campaigns for comparison:", error)
      setLocalError(error.message || "Failed to fetch campaigns for comparison")
      setAvailableCampaigns([])
    } finally {
      setLocalLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      // Reset all states when dialog opens
      setSelectedCampaignIds([])
      setCommonNumbers([])
      setLocalError(null)

      if (setDownloadLink && typeof setDownloadLink === "function") {
        setDownloadLink(null)
      }

      // Fetch campaigns for comparison
      fetchCampaignsForComparison()
    }
  }, [open, currentCampaignId, currentCampaignUserId, userInfo?.token])

  // Effect to update common numbers and generate download link when comparison is completed
  useEffect(() => {
    if (!isComparing && commonNumbers.length > 0) {
      const csvContent = "data:text/csv;charset=utf-8," + commonNumbers.join("\n")
      const encodedUri = encodeURI(csvContent)
      if (setDownloadLink && typeof setDownloadLink === "function") {
        setDownloadLink(encodedUri)
      }
    } else {
      if (setDownloadLink && typeof setDownloadLink === "function") {
        setDownloadLink(null)
      }
    }
  }, [isComparing, commonNumbers, setDownloadLink])

  const handleCheckboxChange = (event) => {
    const campaignId = event.target.name
    if (event.target.checked) {
      setSelectedCampaignIds((prev) => [...prev, campaignId])
    } else {
      setSelectedCampaignIds((prev) => prev.filter((id) => id !== campaignId))
    }
  }

  const handleCompareClick = async () => {
    if (selectedCampaignIds.length === 0) {
      toast.error("Please select at least one campaign to compare.")
      return
    }

    try {
      // onCompare is expected to dispatch the thunk and return the payload on success,
      // or throw an error on rejection.
      const comparisonResult = await onCompare(selectedCampaignIds)

      // If we reach here, the thunk was fulfilled.
      // Check the structure of the fulfilled payload.
      if (comparisonResult && comparisonResult.campaign && comparisonResult.campaign.commonNumbersList) {
        setCommonNumbers(comparisonResult.campaign.commonNumbersList)
        toast.success(comparisonResult.message || "Comparison successful!")
      } else {
        // This case means the thunk fulfilled, but the payload structure was not as expected.
        // This is an unexpected success scenario, but we should handle it gracefully.
        setCommonNumbers([])
        //toast.error("Comparison completed, but result data was unexpected.")
        //console.log("Unexpected comparison result:", comparisonResult)
      }
    } catch (err) {
      // The thunk was rejected, and the error was thrown.
      setCommonNumbers([]) // Clear common numbers on failure
      const errorMessage = err.message || "Failed to compare campaigns."
      toast.error(errorMessage)
      console.error("Comparison error:", err)
    }
  }

  const handleClose = () => {
    // Reset states when closing
    setSelectedCampaignIds([])
    setCommonNumbers([])
    setLocalError(null)
    setAvailableCampaigns([])

    if (setDownloadLink && typeof setDownloadLink === "function") {
      setDownloadLink(null)
    }

    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Compare Campaign
        <IconButton aria-label="close" onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select one or more previously completed campaigns to find common numbers with the current campaign.
        </Typography>

        {localLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <CircularProgress />
          </Box>
        ) : localError ? (
          <Alert severity="error">{localError}</Alert>
        ) : availableCampaigns.length > 0 ? (
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader aria-label="compare campaigns table">
              <TableHead>
                <TableRow>
                  <TableCell>SR. NO.</TableCell>
                  <TableCell>Campaign Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Numbers</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableCampaigns.map((camp, index) => (
                  <TableRow key={camp._id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{camp.title}</TableCell>
                    <TableCell>{new Date(camp.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>{camp.totalNumbers || camp.originalNumbers?.length || 0}</TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedCampaignIds.includes(camp._id)}
                            onChange={handleCheckboxChange}
                            name={camp._id}
                            disabled={isComparing}
                          />
                        }
                        label="Select"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography color="text.secondary">No completed campaigns available for comparison.</Typography>
        )}

        {commonNumbers.length > 0 && (
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">Found {commonNumbers.length} common numbers.</Typography>

            {downloadLink && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{ mt: 1 }}
                href={downloadLink}
                download={`common_numbers_${currentCampaignId.substring(0, 6)}.csv`}
              >
                Download Common Numbers CSV
              </Button>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isComparing}>
          Cancel
        </Button>
        <Button
          onClick={() => {
            handleClose()
            toast.info("Campaign comparison skipped.")
          }}
          disabled={isComparing}
        >
          Skip Compare
        </Button>
        <Button
          onClick={handleCompareClick}
          color="primary"
          variant="contained"
          disabled={selectedCampaignIds.length === 0 || isComparing}
          startIcon={isComparing ? <CircularProgress size={20} /> : null}
        >
          {isComparing ? "Comparing..." : "Compare Now"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CompareCampaignsDialog
